import { ChangeEvent, useEffect, useState } from "react";
import IOnProcessVendorSourceDTO from "../../../models/DTO/IOnProcessVendorSourceDTO";
import IProcessVendorSourceDTO from "../../../models/DTO/IProcessVendorSourceDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import INewVendor_Source from "../../../models/INewVendor_Source";
import INotification from "../../../models/INotification";
import IVendor_Source from "../../../models/IVendor_Source";
import LeadTimeTypeEnum from "../../../models/LeadTimeTypeEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import AddressBlock from "../../shared/AddressBlock/AddressBlock";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../shared/OwTitle/OwTitle";
import "./VendorSourceFnSidebar.scss";
import { useAppContextNonNull } from "@contexts/AppContext";
import IUiSocketSetup from "@models/IUiSocketSetup";

type Props = {
    cancelFn: (refresh?: boolean) => void;
    vendorSource?: IVendor_Source;
};

export const VendorSourceFnSidebar = (props: Props) => {
    const { userModel, supplierModel, socket, uiContext } = useAppContextNonNull();

    const [vendorSource, setVendorSource] = useState<IVendor_Source | INewVendor_Source>(
        props.vendorSource || ({ Bill_To: {}, Ship_To: {} } as INewVendor_Source)
    );
    const [isSaveActive, setIsSaveActive] = useState<boolean>(false);

    const onProcessVendorSource = (data: IOnProcessVendorSourceDTO) => {
        const notification: INotification = {
            message: "successfully_processed_vendor_source",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);
        props.cancelFn(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.PurchaseOrderOnProcessVendorSource, fn: onProcessVendorSource }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const cancel = () => {
        props.cancelFn(false);
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        setVendorSource((prevVendorSource) => {
            const updatedVendorSource = { ...prevVendorSource };
            // @ts-expect-error
            updatedVendorSource[name] = value;
            return updatedVendorSource;
        });
    };

    const handleAddressChange = (e: ChangeEvent<any>, type: "Bill_To" | "Ship_To") => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;
        const name = e.target.name;

        setVendorSource((prevVendorSource) => {
            const updatedVendorSource = { ...prevVendorSource };
            // @ts-expect-error
            updatedVendorSource[type][name] = value;
            return updatedVendorSource;
        });
    };

    const save = () => {
        const payload: IProcessVendorSourceDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorSource: {
                ...vendorSource,
            },
        };

        socket.emit(EndpointsEnum.PurchaseOrderProcessVendorSource, payload);
        setIsSaveActive(true);
    };

    const copyBillToShipTo = () => {
        setVendorSource((prevVendorSource) => ({
            ...prevVendorSource,
            Ship_To: {
                ...prevVendorSource.Bill_To,
            },
        }));
    };

    const lead_time_options = [
        { value: LeadTimeTypeEnum.DAYS, title: "Day(s)" },
        { value: LeadTimeTypeEnum.WEEKS, title: "Week(s)" },
        { value: LeadTimeTypeEnum.MONTHS, title: "Month(s)" },
    ];

    return (
        <div className="VendorSourceSidebar Fn p-2">
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-handshake"} title={"vendor_source"} />
                    <hr />
                </div>
            </div>

            <div>
                <div>
                    <OwLabel title={"name"} />
                    <OwInputWrapper name={"name"} fn={handleChange} value={vendorSource.name} />
                </div>

                <div>
                    <OwLabel title={"customer_no"} />
                    <OwInputWrapper name={"customer_no"} fn={handleChange} value={vendorSource.customer_no} />
                </div>

                <div>
                    <OwLabel title={"email"} />
                    <OwInputWrapper name={"email"} fn={handleChange} value={vendorSource.email} type={"email"} />
                </div>

                <div>
                    <OwLabel title={"bill_to"} />
                    <AddressBlock
                        showCompanyName={true}
                        showName={true}
                        address={vendorSource.Bill_To}
                        fn={(e: any) => {
                            handleAddressChange(e, "Bill_To");
                        }}
                    />
                </div>

                <div>
                    <div className={"d-flex align-items-center mb-2"}>
                        <OwLabel title={"ship_to"} />{" "}
                        <div className={"ms-2"}>
                            <OwButton title={"copy_bill_to_ship_to"} fn={copyBillToShipTo} />
                        </div>
                    </div>
                    <AddressBlock
                        showCompanyName={true}
                        showName={true}
                        address={vendorSource.Ship_To}
                        fn={(e: any) => {
                            handleAddressChange(e, "Ship_To");
                        }}
                    />
                </div>
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"lead_time"} />
                <div className={"d-flex"}>
                    <div className={"w-30"}>
                        <OwInputWrapper
                            title={"default_lead_time"}
                            name={"default_lead_time"}
                            fn={handleChange}
                            value={vendorSource.default_lead_time}
                        />
                    </div>
                    <div className={"ms-2"}>
                        <OwSelectOption
                            selectedOption={vendorSource}
                            selectedOptionProp={"default_lead_time_type"}
                            options={lead_time_options}
                            valueProp={"value"}
                            titleProp={"title"}
                            fn={handleChange}
                            name={"default_lead_time_type"}
                            showBlankFirst={true}
                        />
                    </div>
                </div>
            </div>

            <div>
                <OwButton title={"save"} fn={save} active={isSaveActive} disableOnActive={true} />
            </div>
        </div>
    );
};
